import { useState } from 'react';
import { Outlet, Link } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Sidebar } from '@/components/layout/sidebar';
import { Breadcrumbs } from '@/components/shared/breadcrumbs';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { TrinetraLoop3 } from '@/components/icons/trinetra-icons';
import { AgentActivityProvider } from '@/providers/agent-activity-provider';
import { AgentActivityIndicator } from '@/components/layout/agent-activity-indicator';
import { Toaster } from '@/components/ui/sonner';

/**
 * App shell: persistent sidebar (md+), mobile-drawer sidebar (<md), and
 * the routed page content. There is no topbar — the sidebar absorbs
 * the org switcher, navigation, notifications, and the user menu, so
 * the entire top edge of the content area is reclaimed for breadcrumbs
 * + page content.
 */
export function AppLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <AgentActivityProvider>
      <div className="flex h-screen bg-background">
        {/* Persistent sidebar on md+ */}
        <Sidebar />

        {/* Mobile drawer renders the same Sidebar inside a sheet */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetContent side="left" className="p-0 w-64">
            <Sidebar />
          </SheetContent>
        </Sheet>

        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Mobile-only thin bar with the menu trigger + brand. md+ has
              no topbar at all — the sidebar carries everything. */}
          <header className="md:hidden h-12 border-b border-border bg-card flex items-center gap-3 px-3">
            <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(true)}>
              <Menu className="h-5 w-5" />
            </Button>
            <Link to="/home" className="flex items-center gap-2">
              <TrinetraLoop3 className="h-5 w-5 text-primary-strong" />
              <span className="font-display font-semibold tracking-wide text-foreground">Trinetra</span>
            </Link>
          </header>

          <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
            <Breadcrumbs />
            <Outlet />
          </main>
        </div>
      </div>

      {/* Agent activity indicator: floats at the top-right of the
          viewport so its dropdown has room to open downward without
          fighting the sidebar's edge. Stays above scrollable content
          via fixed positioning + z-40. */}
      <div className="fixed top-3 right-3 md:top-4 md:right-4 z-40">
        <AgentActivityIndicator />
      </div>

      <Toaster />
    </AgentActivityProvider>
  );
}
